(() => {
  if (!customElements.get('minimal-product-gallery')) {
    customElements.define('minimal-product-gallery', class extends HTMLElement {
      connectedCallback() {
        if (this.controller) return;
        this.controller = new AbortController();
        const options = { signal: this.controller.signal };
        this.product = this.closest('[data-main-product]');
        this.dialog = this.querySelector('dialog');
        this.viewport = this.querySelector('[data-gallery-viewport]');
        this.surface = this.querySelector('[data-gallery-surface]');
        this.image = this.querySelector('[data-gallery-image]');
        this.counter = this.querySelector('[data-gallery-counter]');
        this.zoomButton = this.querySelector('[data-gallery-zoom]');
        this.loading = this.querySelector('[data-gallery-loading]');
        this.error = this.querySelector('[data-gallery-error]');
        this.sources = [...this.product.querySelectorAll('[data-gallery-open]')];
        this.currentTrigger = this.product.querySelector('[data-gallery-open-current]');
        this.index = 0;
        this.zoomed = false;
        this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
        let openerStart;
        let openerMoved = false;
        this.product.addEventListener('pointerdown', (event) => {
          if (!event.target.closest('[data-gallery-open], [data-gallery-open-current]')) return;
          openerStart = { x: event.clientX, y: event.clientY };
          openerMoved = false;
        }, options);
        this.product.addEventListener('pointermove', (event) => {
          if (openerStart && Math.hypot(event.clientX - openerStart.x, event.clientY - openerStart.y) > 8) openerMoved = true;
        }, options);
        this.product.addEventListener('pointercancel', () => { openerMoved = true; }, options);
        this.product.addEventListener('click', (event) => {
          const trigger = event.target.closest('[data-gallery-open], [data-gallery-open-current]');
          if (!trigger || (!this.sources.includes(trigger) && trigger !== this.currentTrigger)) return;
          event.preventDefault();
          if (openerMoved && event.detail !== 0) return;
          this.trigger = trigger;
          this.open(trigger === this.currentTrigger ? this.currentImageIndex() : Number(trigger.dataset.galleryIndex));
        }, options);
        this.querySelector('[data-gallery-close]').addEventListener('click', () => this.close(), options);
        this.dialog.addEventListener('cancel', (event) => {
          event.preventDefault();
          this.close();
        }, options);
        this.querySelector('[data-gallery-previous]').addEventListener('click', () => this.show(this.index - 1), options);
        this.querySelector('[data-gallery-next]').addEventListener('click', () => this.show(this.index + 1), options);
        this.zoomButton.addEventListener('click', () => this.setZoom(!this.zoomed), options);
        this.dialog.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            event.preventDefault();
            this.show(this.index + (event.key === 'ArrowRight' ? 1 : -1));
          }
        }, options);
        this.dialog.addEventListener('close', () => {
          this.cancelAnimation();
          this.setZoom(false);
          if (!document.querySelector('minimal-product-gallery dialog[open]')) document.documentElement.classList.remove('minimal-product-gallery-open');
          if (this.trigger?.isConnected) this.trigger.focus({ preventScroll: true });
        }, options);
        this.image.addEventListener('load', () => {
          this.loading.hidden = true;
          this.error.hidden = true;
          this.image.hidden = false;
          this.zoomButton.disabled = false;
          this.fitImage();
        }, options);
        this.image.addEventListener('error', () => {
          this.loading.hidden = true;
          this.error.hidden = false;
          this.image.hidden = true;
          this.zoomButton.disabled = true;
        }, options);
        let gesture;
        let moved = false;
        this.viewport.addEventListener('pointerdown', (event) => {
          if (event.button !== 0) return;
          gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, left: this.viewport.scrollLeft, top: this.viewport.scrollTop, mouse: event.pointerType === 'mouse' };
          moved = false;
        }, options);
        this.viewport.addEventListener('pointermove', (event) => {
          if (event.pointerId !== gesture?.id) return;
          const dx = event.clientX - gesture.x;
          const dy = event.clientY - gesture.y;
          if (Math.hypot(dx, dy) > 8) moved = true;
          if (!this.zoomed || !gesture.mouse || !moved) return;
          event.preventDefault();
          this.viewport.setPointerCapture(event.pointerId);
          this.dialog.classList.add('is-panning');
          this.viewport.scrollLeft = gesture.left - dx;
          this.viewport.scrollTop = gesture.top - dy;
        }, options);
        this.viewport.addEventListener('pointerup', (event) => {
          if (event.pointerId !== gesture?.id) return;
          const dx = event.clientX - gesture.x;
          const dy = event.clientY - gesture.y;
          if (!this.zoomed && Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.2) this.show(this.index + (dx < 0 ? 1 : -1));
          if (this.viewport.hasPointerCapture(event.pointerId)) this.viewport.releasePointerCapture(event.pointerId);
          gesture = null;
          this.dialog.classList.remove('is-panning');
        }, options);
        this.viewport.addEventListener('pointercancel', () => {
          gesture = null;
          moved = true;
          this.dialog.classList.remove('is-panning');
        }, options);
        this.image.addEventListener('click', () => {
          if (!moved && !this.zoomed) this.setZoom(true);
        }, options);
        this.image.addEventListener('dragstart', (event) => event.preventDefault(), options);
        window.addEventListener('pagehide', () => this.close(true), options);
        this.motion.addEventListener('change', () => {
          if (!this.motion.matches) return;
          if (this.closing) this.close(true);
          else this.cancelAnimation();
        }, options);
        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(() => { if (this.dialog.open) this.fitImage(); });
          this.resizeObserver.observe(this.viewport);
        }
      }
      disconnectedCallback() {
        this.cancelAnimation();
        if (this.dialog?.open) this.dialog.close();
        this.resizeObserver?.disconnect();
        this.controller?.abort();
        this.controller = null;
      }
      currentImageIndex() {
        const media = this.product.querySelector('.main-product__media');
        if (!media) return 0;
        const rect = media.getBoundingClientRect();
        const vertical = this.product.classList.contains('main-product--media-vertical') && !window.matchMedia('(max-width: 749px)').matches;
        const center = vertical
          ? (Math.max(0, rect.top) + Math.min(window.innerHeight, rect.bottom)) / 2
          : rect.left + rect.width / 2;
        let nearest = 0;
        let distance = Infinity;
        this.sources.forEach((source, index) => {
          const frame = source.closest('.main-product__media-item').getBoundingClientRect();
          const offset = Math.abs((vertical ? frame.top + frame.height / 2 : frame.left + frame.width / 2) - center);
          if (offset < distance) { nearest = index; distance = offset; }
        });
        return nearest;
      }
      open(index) {
        if (!this.sources.length) return;
        if (this.dialog.open) {
          if (!this.closing) return;
          this.cancelAnimation();
          this.show(index);
          return;
        }
        this.dialog.showModal();
        document.documentElement.classList.add('minimal-product-gallery-open');
        this.show(index);
        if (!this.motion.matches && this.dialog.animate) {
          this.dialogAnimation = this.dialog.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 260, easing: 'cubic-bezier(.22, 1, .36, 1)' });
        }
      }
      cancelAnimation() {
        this.dialogAnimation?.cancel();
        this.dialogAnimation = null;
        this.closing = false;
        this.dialog?.classList.remove('is-closing');
      }
      close(immediate = false) {
        if (!this.dialog?.open || this.closing && !immediate) return;
        const opacity = this.dialogAnimation ? getComputedStyle(this.dialog).opacity : '1';
        this.cancelAnimation();
        if (immediate || this.motion.matches || !this.dialog.animate) {
          this.dialog.close();
          return;
        }
        this.closing = true;
        this.dialog.classList.add('is-closing');
        const animation = this.dialog.animate([{ opacity }, { opacity: 0 }], { duration: 260, easing: 'cubic-bezier(.22, 1, .36, 1)', fill: 'forwards' });
        this.dialogAnimation = animation;
        animation.finished.then(() => {
          if (this.dialogAnimation === animation) this.dialog.close();
        }).catch(() => {});
      }
      show(index) {
        if (!this.sources.length) return;
        this.index = (index + this.sources.length) % this.sources.length;
        const source = this.sources[this.index];
        this.setZoom(false);
        this.image.hidden = true;
        this.loading.hidden = false;
        this.error.hidden = true;
        this.zoomButton.disabled = true;
        this.image.alt = source.dataset.galleryAlt;
        this.counter.textContent = `${String(this.index + 1).padStart(2, '0')} / ${String(this.sources.length).padStart(2, '0')}`;
        this.counter.setAttribute('aria-label', this.dataset.position.replace('[current]', String(this.index + 1)).replace('[total]', String(this.sources.length)));
        this.image.src = source.dataset.gallerySrc;
        if (this.image.complete && this.image.naturalWidth) {
          this.image.hidden = false;
          this.loading.hidden = true;
          this.zoomButton.disabled = false;
        }
        this.fitImage();
      }
      fitImage() {
        if (!this.dialog.open) return;
        const source = this.sources[this.index];
        const sourceWidth = Number(source?.dataset.galleryWidth) || 1;
        const sourceHeight = Number(source?.dataset.galleryHeight) || 1;
        const space = this.viewport.clientWidth < 750 ? 24 : 64;
        const ratio = Math.min(Math.max(1, this.viewport.clientWidth - space) / sourceWidth, Math.max(1, this.viewport.clientHeight - space) / sourceHeight, 1);
        const width = sourceWidth * ratio * (this.zoomed ? 2 : 1);
        const height = sourceHeight * ratio * (this.zoomed ? 2 : 1);
        this.image.style.width = `${width}px`;
        this.image.style.height = `${height}px`;
        this.surface.style.width = `${Math.max(width, this.viewport.clientWidth)}px`;
        this.surface.style.height = `${Math.max(height, this.viewport.clientHeight)}px`;
      }
      setZoom(zoomed) {
        this.zoomed = Boolean(zoomed);
        this.dialog.classList.toggle('is-zoomed', this.zoomed);
        this.zoomButton.setAttribute('aria-pressed', String(this.zoomed));
        this.zoomButton.setAttribute('aria-label', this.zoomed ? this.dataset.zoomOut : this.dataset.zoomIn);
        this.zoomButton.querySelector('path').setAttribute('d', this.zoomed ? 'm15 15 6 6M7 10h6' : 'm15 15 6 6M7 10h6m-3-3v6');
        this.fitImage();
        this.viewport.scrollLeft = this.zoomed ? (this.viewport.scrollWidth - this.viewport.clientWidth) / 2 : 0;
        this.viewport.scrollTop = this.zoomed ? (this.viewport.scrollHeight - this.viewport.clientHeight) / 2 : 0;
      }
    });
  }

  if (!customElements.get('minimal-product-information')) {
    customElements.define('minimal-product-information', class extends HTMLElement {
      connectedCallback() {
        if (this.controller) return;
        this.controller = new AbortController();
        const options = { signal: this.controller.signal };
        this.closeTimers = new Map();
        this.frames = new Map();
        this.mobile = window.matchMedia('(max-width: 749px)');
        this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.rows = [...this.querySelectorAll('details')];
        this.rows.forEach((row) => {
          const open = row.dataset.infoDesktopOpen === undefined ? row.open : row.dataset[this.mobile.matches ? 'infoMobileOpen' : 'infoDesktopOpen'] === 'true';
          row.dataset.infoReady = 'true';
          this.setOpen(row, open, false);
          row.querySelector('summary').addEventListener('click', (event) => {
            event.preventDefault();
            this.setOpen(row, !row.classList.contains('is-expanded'));
          }, options);
        });
        this.mobile.addEventListener('change', () => {
          this.rows.filter(row => row.dataset.infoDesktopOpen !== undefined).forEach(row => this.setOpen(row, row.dataset[this.mobile.matches ? 'infoMobileOpen' : 'infoDesktopOpen'] === 'true', false));
        }, options);
        document.addEventListener('shopify:block:select', (event) => {
          const row = this.rows.find(row => row.dataset.infoBlockId === event.detail?.blockId);
          if (row) this.setOpen(row, true, false);
        }, options);
      }
      disconnectedCallback() {
        this.closeTimers?.forEach(timer => clearTimeout(timer));
        this.frames?.forEach(frame => cancelAnimationFrame(frame));
        this.controller?.abort();
        this.controller = null;
      }
      setOpen(row, open, animate = true) {
        clearTimeout(this.closeTimers.get(row));
        cancelAnimationFrame(this.frames.get(row));
        row.querySelector('summary').setAttribute('aria-expanded', String(open));
        row.querySelector('.main-product__info-reveal').inert = !open;
        if (open) {
          row.open = true;
          if (animate && !this.motion.matches) {
            this.frames.set(row, requestAnimationFrame(() => row.classList.add('is-expanded')));
          } else row.classList.add('is-expanded');
        } else {
          row.classList.remove('is-expanded');
          if (animate && !this.motion.matches) this.closeTimers.set(row, setTimeout(() => { row.open = false; }, 300));
          else row.open = false;
        }
      }
    });
  }
})();
